/**
 * Store Locator JavaScript
 * Stockist-like functionality for finding stores
 */

class StoreLocator {
  constructor() {
    this.stores = [];
    this.filteredStores = [];
    this.visibleStores = [];
    this.userLocation = null;
    this.map = null;
    this.markers = [];
    this.infoWindows = [];
    this.activeStoreId = null;
    this.searchRadius = 25;
    this.mapBoundsListener = null;
    this.autocomplete = null;
    this.markerClusterer = null;

    this.init();
  }

  init() {
    // Load store data
    this.loadStoreData();

    // Set up event listeners
    this.setupEventListeners();

    // Initialize map if API key is available
    this.initializeMap();
  }

  loadStoreData() {
    const dataElement = document.getElementById('store-locations-data');
    if (dataElement) {
      const data = JSON.parse(dataElement.textContent);
      this.stores = data.stores;
      this.apiKey = data.api_key;

      // Show all stores initially
      this.displayStores(this.stores);
    }
  }

  setupEventListeners() {
    // Search button
    const searchBtn = document.getElementById('store-search-btn');
    if (searchBtn) {
      searchBtn.addEventListener('click', () => this.handleSearch());
    }

    // Enter key on search input
    const searchInput = document.getElementById('store-search-input');
    if (searchInput) {
      searchInput.addEventListener('keypress', (e) => {
        if (e.key === 'Enter') {
          this.handleSearch();
        }
      });

      // Handle clearing the search input
      searchInput.addEventListener('input', (e) => {
        if (e.target.value.trim() === '') {
          this.handleClearSearch();
        }
      });
    }

    // Use location button
    const locationBtn = document.getElementById('use-location-btn');
    if (locationBtn) {
      locationBtn.addEventListener('click', () => this.getUserLocation());
    }

    // Radius filter
    const radiusFilter = document.getElementById('radius-filter');
    if (radiusFilter) {
      radiusFilter.addEventListener('change', (e) => {
        this.searchRadius = parseInt(e.target.value);
        if (this.userLocation) {
          this.filterStoresByDistance();
        }
      });
    }
  }

  async handleSearch() {
    const searchInput = document.getElementById('store-search-input');
    const query = searchInput.value.trim();

    if (!query) {
      this.handleClearSearch();
      return;
    }

    this.showLoading(true);

    // Check if query is a zip code
    if (/^\d{5}$/.test(query)) {
      await this.searchByZipCode(query);
    } else {
      // Use geocoding for address/city search
      await this.geocodeAddress(query);
    }
  }

  async searchByZipCode(zipCode) {
    // Filter stores by zip code
    const matchingStores = this.stores.filter(store =>
      store.zip.includes(zipCode)
    );

    if (matchingStores.length > 0) {
      // Get coordinates of first matching store
      const firstStore = matchingStores[0];
      this.userLocation = {
        lat: firstStore.lat,
        lng: firstStore.lng
      };
      this.filterStoresByDistance();
    } else {
      // Try geocoding the zip code
      await this.geocodeAddress(zipCode);
    }

    this.showLoading(false);
  }

  async geocodeAddress(address) {
    if (!this.apiKey || !window.google) {
      // Fallback: search by text in store data
      this.searchByText(address);
      return;
    }

    try {
      const geocoder = new google.maps.Geocoder();
      geocoder.geocode({ address: address }, (results, status) => {
        if (status === 'OK' && results[0]) {
          const location = results[0].geometry.location;
          this.userLocation = {
            lat: location.lat(),
            lng: location.lng()
          };
          this.filterStoresByDistance();

          // Center map on geocoded location
          if (this.map) {
            this.map.setCenter(this.userLocation);
            this.map.setZoom(11);
          }
        } else {
          this.searchByText(address);
        }
        this.showLoading(false);
      });
    } catch (error) {
      this.searchByText(address);
      this.showLoading(false);
    }
  }

  searchByText(query) {
    const searchTerm = query.toLowerCase();
    const filtered = this.stores.filter(store => {
      return (
        store.name.toLowerCase().includes(searchTerm) ||
        store.city.toLowerCase().includes(searchTerm) ||
        store.state.toLowerCase().includes(searchTerm) ||
        store.address.toLowerCase().includes(searchTerm) ||
        store.zip.includes(searchTerm)
      );
    });

    this.filteredStores = filtered;
    this.deselectStore();
    this.displayStores(filtered);
  }

  getUserLocation() {
    if (!navigator.geolocation) {
      alert('Geolocation is not supported by your browser');
      return;
    }

    this.showLoading(true);

    navigator.geolocation.getCurrentPosition(
      (position) => {
        this.userLocation = {
          lat: position.coords.latitude,
          lng: position.coords.longitude
        };
        this.filterStoresByDistance();
        this.showLoading(false);

        // Update search input with "Current Location"
        const searchInput = document.getElementById('store-search-input');
        if (searchInput) {
          searchInput.value = 'Current Location';
        }
      },
      (error) => {
        this.showLoading(false);
        alert('Unable to get your location. Please enter a location manually.');
      }
    );
  }

  filterStoresByDistance() {
    if (!this.userLocation) return;

    // Calculate distances
    const storesWithDistance = this.stores.map(store => {
      const distance = this.calculateDistance(
        this.userLocation.lat,
        this.userLocation.lng,
        store.lat,
        store.lng
      );
      return { ...store, distance };
    });

    // Filter by radius
    this.filteredStores = storesWithDistance
      .filter(store => store.distance <= this.searchRadius)
      .sort((a, b) => a.distance - b.distance);

    // Clear active selection when filtering
    this.deselectStore();

    this.displayStores(this.filteredStores);
  }

  calculateDistance(lat1, lon1, lat2, lon2) {
    const R = 3959; // Radius of Earth in miles
    const dLat = this.toRad(lat2 - lat1);
    const dLon = this.toRad(lon2 - lon1);
    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(this.toRad(lat1)) * Math.cos(this.toRad(lat2)) *
      Math.sin(dLon / 2) * Math.sin(dLon / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
  }

  toRad(value) {
    return value * Math.PI / 180;
  }

  displayStores(stores, updateMap = true) {
    const storesList = document.getElementById('stores-list');
    const noResults = document.querySelector('.no-results');
    const resultsCount = document.querySelector('.results-count');
    const resultsText = document.getElementById('results-count-text');

    if (!storesList) return;

    // Clear existing content
    storesList.innerHTML = '';

    if (stores.length === 0) {
      noResults.style.display = 'block';
      resultsCount.style.display = 'none';
      return;
    }

    noResults.style.display = 'none';

    // Update results count
    if (resultsCount && resultsText) {
      resultsCount.style.display = 'block';
      resultsText.textContent = `${stores.length} store${stores.length !== 1 ? 's' : ''} found`;
    }

    // Sort stores by distance if available
    const sortedStores = [...stores];
    sortedStores.sort((a, b) => (a.distance || 0) - (b.distance || 0));

    // Create store list items
    sortedStores.forEach((store, index) => {
      const listItem = this.createStoreListItem(store, index);
      storesList.appendChild(listItem);
    });

    // Update map only if requested
    if (updateMap) {
      this.updateMap();
    }
  }

  createStoreListItem(store, index) {
    const item = document.createElement('div');
    item.className = 'store-list-item';
    item.dataset.storeId = store.id;
    item.dataset.storeIndex = index;

    const distanceHtml = store.distance
      ? `<div class="store-list-distance">${store.distance.toFixed(1)} miles</div>`
      : '';

    // Get current day and hours
    const days = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
    const today = days[new Date().getDay()];
    const todayHours = store.hours[today];

    item.innerHTML = `
      <div class="store-list-header">
        <div class="store-list-info">
          <h3 class="store-list-name">${store.name}</h3>
          ${distanceHtml}
        </div>
      </div>

      <div class="store-list-address">
        ${store.address}, ${store.city}, ${store.state} ${store.zip}
      </div>

      <div class="store-list-phone">
        <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
          <path d="M2 3C2 2.44772 2.44772 2 3 2H5.15287C5.64171 2 6.0589 2.35341 6.13927 2.8356L6.87858 7.27147C6.95075 7.70451 6.73206 8.13397 6.3394 8.3303L4.79126 9.10437C5.90756 11.8783 8.12168 14.0924 10.8956 15.2087L11.6697 13.6606C11.866 13.2679 12.2955 13.0492 12.7285 13.1214L17.1644 13.8607C17.6466 13.9411 18 14.3583 18 14.8471V17C18 17.5523 17.5523 18 17 18H15C7.26801 18 1 11.732 1 4V3Z" stroke="currentColor" stroke-width="1.5"/>
        </svg>
        <a href="tel:${store.phone}">${store.phone}</a>
      </div>

      <div class="store-list-hours">
        <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
          <circle cx="8" cy="8" r="7" stroke="currentColor" stroke-width="1.5"/>
          <path d="M8 4V8L11 11" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
        </svg>
        <span>Today: ${todayHours}</span>
      </div>
    `;

    // Add click handler to focus on map
    item.addEventListener('click', () => {
      this.activeStoreId = store.id;
      this.focusStore(store);
      this.setActiveListItem(store.id);

      // Show only this store in the list
      this.displayStores([store], false);
    });

    return item;
  }

  setActiveListItem(storeId) {
    // Remove active class from all items
    document.querySelectorAll('.store-list-item').forEach(item => {
      item.classList.remove('active');
    });

    // Add active class to selected item
    const activeItem = document.querySelector(`[data-store-id="${storeId}"]`);
    if (activeItem) {
      activeItem.classList.add('active');

      // Scroll the item into view if needed
      activeItem.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }

  showStoreDetails(storeId) {
    const store = this.stores.find(s => s.id === storeId);
    if (!store) return;

    // Create modal or expand card with full hours
    const hoursHtml = Object.entries(store.hours)
      .map(([day, hours]) => {
        const dayName = day.charAt(0).toUpperCase() + day.slice(1);
        return `<div class="hours-row">
          <span class="day">${dayName}:</span>
          <span class="hours">${hours}</span>
        </div>`;
      })
      .join('');

    // You could create a modal here or navigate to a detail page
    alert(`Store Details:\n\n${store.name}\n${store.address}\n${store.city}, ${store.state} ${store.zip}\n\nFull implementation would show a modal with complete hours and details.`);
  }


  initializeMap() {
    if (!this.apiKey || !window.google) {
      console.log('Google Maps API not loaded');
      return;
    }

    const mapElement = document.getElementById('store-map');
    if (!mapElement) return;

    // Check if mobile device
    const isMobile = window.innerWidth <= 768;

    // Initialize map centered on US with mobile-optimized zoom
    this.map = new google.maps.Map(mapElement, {
      zoom: isMobile ? 6 : 4, // Higher zoom on mobile for better visibility
      center: { lat: 39.8283, lng: -98.5795 }, // Center of US
      styles: this.getMapStyles()
    });

    // Add map event listeners
    this.setupMapListeners();

    // Initialize Places Autocomplete
    this.initializeAutocomplete();

    this.updateMap();
  }

  initializeAutocomplete() {
    if (!window.google || !window.google.maps.places) {
      console.log('Google Places API not loaded');
      return;
    }

    const searchInput = document.getElementById('store-search-input');
    if (!searchInput) return;

    // Create autocomplete instance
    this.autocomplete = new google.maps.places.Autocomplete(searchInput, {
      types: ['geocode'], // Limit to addresses and locations
      componentRestrictions: { country: 'us' }, // Limit to US addresses
      fields: ['geometry', 'formatted_address', 'address_components']
    });

    // Listen for place selection
    this.autocomplete.addListener('place_changed', () => {
      const place = this.autocomplete.getPlace();

      if (!place.geometry || !place.geometry.location) {
        // User entered a place that was not suggested
        this.handleSearch();
        return;
      }

      // Get location from selected place
      this.userLocation = {
        lat: place.geometry.location.lat(),
        lng: place.geometry.location.lng()
      };

      // Update search input with formatted address
      searchInput.value = place.formatted_address || searchInput.value;

      // Filter stores by distance from this location
      this.filterStoresByDistance();

      // Center map on this location
      if (this.map) {
        this.map.setCenter(this.userLocation);
        this.map.setZoom(11);
      }
    });

    // Prevent form submission on Enter when autocomplete is active
    searchInput.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') {
        const container = document.querySelector('.pac-container');
        if (container && container.style.display !== 'none') {
          e.preventDefault();
        }
      }
    });
  }

  setupMapListeners() {
    if (!this.map) return;

    // Listen for map bounds changes (pan, zoom)
    this.map.addListener('idle', () => {
      if (!this.activeStoreId) {
        this.updateVisibleStores();
      }
    });

    // Click on map (not on markers) to deselect
    this.map.addListener('click', () => {
      this.deselectStore();
    });
  }

  updateVisibleStores() {
    if (!this.map) return;

    const bounds = this.map.getBounds();
    if (!bounds) return;

    // Get stores that are currently visible in the map viewport
    const currentStores = this.filteredStores.length > 0 ? this.filteredStores : this.stores;
    this.visibleStores = currentStores.filter(store => {
      const position = new google.maps.LatLng(store.lat, store.lng);
      return bounds.contains(position);
    });

    // Update the list to show only visible stores
    this.displayStores(this.visibleStores, false);
  }

  deselectStore() {
    this.activeStoreId = null;

    // Remove active class from all list items
    document.querySelectorAll('.store-list-item').forEach(item => {
      item.classList.remove('active');
    });

    // Close all info windows
    this.infoWindows.forEach(iw => iw.close());

    // Show all stores in current filter/search results
    const storesToShow = this.filteredStores.length > 0 ? this.filteredStores : this.stores;
    this.displayStores(storesToShow, false);
  }

  handleClearSearch() {
    // Reset filters and location
    this.userLocation = null;
    this.filteredStores = [];
    this.activeStoreId = null;

    // Close all info windows
    this.infoWindows.forEach(iw => iw.close());

    // Show all stores
    this.displayStores(this.stores);

    // Reset map to show all stores
    if (this.map) {
      this.resetMapView();
    }
  }

  resetMapView() {
    if (!this.map || this.stores.length === 0) return;

    const bounds = new google.maps.LatLngBounds();
    const isMobile = window.innerWidth <= 768;

    // Include all store locations
    this.stores.forEach(store => {
      bounds.extend({ lat: store.lat, lng: store.lng });
    });

    // Fit map to show all stores
    this.map.fitBounds(bounds);

    // Apply mobile-specific zoom limits
    setTimeout(() => {
      const currentZoom = this.map.getZoom();
      const maxZoom = isMobile ? 8 : 12; // Less zoom restriction on mobile
      if (currentZoom > maxZoom) {
        this.map.setZoom(maxZoom);
      }
    }, 100);
  }

  updateMap() {
    if (!this.map) return;

    // Clear existing markers and clusterer
    this.clearMarkers();

    const stores = this.filteredStores.length > 0 ? this.filteredStores : this.stores;
    const bounds = new google.maps.LatLngBounds();

    // Add user location marker if available
    if (this.userLocation) {
      const userMarker = new google.maps.Marker({
        position: this.userLocation,
        map: this.map,
        icon: {
          path: google.maps.SymbolPath.CIRCLE,
          scale: 8,
          fillColor: '#4285F4',
          fillOpacity: 1,
          strokeColor: '#ffffff',
          strokeWeight: 2
        },
        title: 'Your Location'
      });
      this.markers.push(userMarker);
      bounds.extend(this.userLocation);
    }

    // Create array to hold store markers for clustering
    const storeMarkers = [];

    // Add store markers
    stores.forEach(store => {
      const position = { lat: store.lat, lng: store.lng };

      const marker = new google.maps.Marker({
        position: position,
        title: store.name,
        icon: {
          url: 'data:image/svg+xml;base64,' + btoa(`
            <svg width="40" height="40" viewBox="0 0 40 40" xmlns="http://www.w3.org/2000/svg">
              <path d="M20 0C12.8 0 7 5.8 7 13C7 23 20 40 20 40S33 23 33 13C33 5.8 27.2 0 20 0Z"
                    fill="#d32f2f"/>
              <circle cx="20" cy="13" r="6" fill="white"/>
            </svg>
          `),
          scaledSize: new google.maps.Size(40, 40)
        }
      });

      // Get current day and hours
      const days = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
      const today = days[new Date().getDay()];
      const todayHours = store.hours[today];

      // Create info window with improved design
      const infoWindow = new google.maps.InfoWindow({
        content: `
          <div class="map-info-window" style="
            padding: 0;
            min-width: 300px;
            max-width: 340px;
            border-radius: 16px;
            overflow: hidden;
            box-shadow: 0 12px 40px rgba(0,0,0,0.15);
            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
            background: white;
          ">
            <!-- Header -->
            <div style="
              background: linear-gradient(135deg, #d32f2f 0%, #b71c1c 100%);
              color: white;
              padding: 20px 24px 18px;
              position: relative;
            ">
              <h4 style="
                margin: 0 0 6px;
                font-size: 18px;
                font-weight: 600;
                line-height: 1.2;
                letter-spacing: -0.02em;
              ">${store.name}</h4>
              ${store.distance ? `
                <div style="
                  font-size: 14px;
                  opacity: 0.95;
                  font-weight: 500;
                  display: flex;
                  align-items: center;
                  gap: 4px;
                ">
                  <span style="font-size: 12px;">📍</span>
                  ${store.distance.toFixed(1)} miles away
                </div>
              ` : ''}
            </div>

            <!-- Content -->
            <div style="padding: 20px 24px;">
              <!-- Address -->
              <div style="
                display: flex;
                align-items: flex-start;
                gap: 12px;
                margin-bottom: 16px;
              ">
                <div style="
                  width: 20px;
                  height: 20px;
                  background: #f8f9fa;
                  border-radius: 50%;
                  display: flex;
                  align-items: center;
                  justify-content: center;
                  margin-top: 2px;
                  flex-shrink: 0;
                ">
                  <svg width="11" height="11" viewBox="0 0 12 12" fill="none">
                    <path d="M6 0C3.79 0 2 1.79 2 4C2 7 6 12 6 12S10 7 10 4C10 1.79 8.21 0 6 0ZM6 5.5C5.17 5.5 4.5 4.83 4.5 4S5.17 2.5 6 2.5S7.5 3.17 7.5 4S6.83 5.5 6 5.5Z" fill="#666"/>
                  </svg>
                </div>
                <div style="
                  color: #333;
                  font-size: 15px;
                  line-height: 1.4;
                  font-weight: 400;
                ">
                  ${store.address}<br>
                  ${store.city}, ${store.state} ${store.zip}
                </div>
              </div>

              <!-- Phone -->
              <div style="
                display: flex;
                align-items: center;
                gap: 12px;
                margin-bottom: 16px;
              ">
                <div style="
                  width: 20px;
                  height: 20px;
                  background: #f8f9fa;
                  border-radius: 50%;
                  display: flex;
                  align-items: center;
                  justify-content: center;
                  flex-shrink: 0;
                ">
                  <svg width="11" height="11" viewBox="0 0 12 12" fill="none">
                    <path d="M1.5 2.25C1.5 1.83579 1.83579 1.5 2.25 1.5H3.61465C3.98127 1.5 4.29417 1.76506 4.35445 2.1267L4.90894 5.45360C4.96306 5.77838 4.79905 6.10048 4.50455 6.24773L3.59345 6.82828C4.31817 8.90872 6.09128 10.6818 8.17172 11.4065L8.75227 10.4955C8.89952 10.2009 9.22162 10.0369 9.54640 10.0911L12.8733 10.6456C13.2349 10.7058 13.5 11.0187 13.5 11.3854V12.75C13.5 13.1642 13.1642 13.5 12.75 13.5H11.25C5.95101 13.5 1.5 9.04899 1.5 3.75V2.25Z" stroke="#666" stroke-width="1"/>
                  </svg>
                </div>
                <a href="tel:${store.phone}" style="
                  color: #d32f2f;
                  text-decoration: none;
                  font-size: 15px;
                  font-weight: 500;
                ">${store.phone}</a>
              </div>

              <!-- Hours -->
              <div style="
                display: flex;
                align-items: center;
                gap: 12px;
                margin-bottom: 20px;
              ">
                <div style="
                  width: 20px;
                  height: 20px;
                  background: #f8f9fa;
                  border-radius: 50%;
                  display: flex;
                  align-items: center;
                  justify-content: center;
                  flex-shrink: 0;
                ">
                  <svg width="11" height="11" viewBox="0 0 12 12" fill="none">
                    <circle cx="6" cy="6" r="5.25" stroke="#666" stroke-width="1"/>
                    <path d="M6 3V6L8.25 8.25" stroke="#666" stroke-width="1" stroke-linecap="round"/>
                  </svg>
                </div>
                <div style="
                  color: #333;
                  font-size: 15px;
                  font-weight: 400;
                ">
                  <strong>Today:</strong> ${todayHours}
                </div>
              </div>

              <!-- Actions -->
              <div style="
                display: flex;
                gap: 10px;
                margin-top: 0px;
              ">
                <a href="https://maps.google.com/?q=${encodeURIComponent(store.address + ', ' + store.city + ', ' + store.state + ' ' + store.zip)}"
                   target="_blank"
                   style="
                     display: flex;
                     align-items: center;
                     justify-content: center;
                     gap: 8px;
                     flex: 1;
                     background: #d32f2f;
                     color: white;
                     text-decoration: none;
                     font-size: 14px;
                     font-weight: 600;
                     padding: 12px 16px;
                     border-radius: 10px;
                     transition: background 0.2s;
                   "
                   onmouseover="this.style.background='#b71c1c'"
                   onmouseout="this.style.background='#d32f2f'">
                  <svg width="13" height="13" viewBox="0 0 12 12" fill="currentColor">
                    <path d="M6 0C3.79 0 2 1.79 2 4C2 7 6 12 6 12S10 7 10 4C10 1.79 8.21 0 6 0ZM6 5.5C5.17 5.5 4.5 4.83 4.5 4S5.17 2.5 6 2.5S7.5 3.17 7.5 4S6.83 5.5 6 5.5Z"/>
                  </svg>
                  Directions
                </a>
                <a href="tel:${store.phone}"
                   style="
                     display: flex;
                     align-items: center;
                     justify-content: center;
                     gap: 8px;
                     background: white;
                     color: #d32f2f;
                     text-decoration: none;
                     font-size: 14px;
                     font-weight: 600;
                     padding: 12px 16px;
                     border: 2px solid #d32f2f;
                     border-radius: 10px;
                     transition: all 0.2s;
                   "
                   onmouseover="this.style.background='#fff5f5'"
                   onmouseout="this.style.background='white'">
                  <svg width="13" height="13" viewBox="0 0 12 12" fill="currentColor">
                    <path d="M1.5 2.25C1.5 1.83579 1.83579 1.5 2.25 1.5H3.61465C3.98127 1.5 4.29417 1.76506 4.35445 2.1267L4.90894 5.45360C4.96306 5.77838 4.79905 6.10048 4.50455 6.24773L3.59345 6.82828C4.31817 8.90872 6.09128 10.6818 8.17172 11.4065L8.75227 10.4955C8.89952 10.2009 9.22162 10.0369 9.54640 10.0911L12.8733 10.6456C13.2349 10.7058 13.5 11.0187 13.5 11.3854V12.75C13.5 13.1642 13.1642 13.5 12.75 13.5H11.25C5.95101 13.5 1.5 9.04899 1.5 3.75V2.25Z"/>
                  </svg>
                  Call
                </a>
              </div>
            </div>
          </div>
        `
      });

      marker.addListener('click', (e) => {
        // Stop propagation to prevent map click
        if (e && e.stop) e.stop();

        // Close all other info windows
        this.infoWindows.forEach(iw => iw.close());
        infoWindow.open(this.map, marker);

        // Set active store and highlight list item
        this.activeStoreId = store.id;
        this.setActiveListItem(store.id);

        // Show only this store in the list when selected
        this.displayStores([store], false);
      });

      // Add close listener to info window
      infoWindow.addListener('closeclick', () => {
        this.deselectStore();
      });

      // Add to store markers array for clustering
      storeMarkers.push(marker);
      this.markers.push(marker);
      this.infoWindows.push(infoWindow);
      bounds.extend(position);
    });

    // Initialize marker clusterer if Google Maps MarkerClusterer is available
    if (window.markerClusterer && storeMarkers.length > 0) {
      this.markerClusterer = new markerClusterer.MarkerClusterer({
        map: this.map,
        markers: storeMarkers,
        algorithm: new markerClusterer.SuperClusterAlgorithm({
          radius: 100,
          maxZoom: 15,
        }),
        renderer: {
          render: ({ count, position }) => {
            // Custom cluster marker styling
            const color = count > 10 ? '#b71c1c' : '#d32f2f';
            const size = count > 10 ? 60 : 50;

            return new google.maps.Marker({
              position,
              icon: {
                url: 'data:image/svg+xml;base64,' + btoa(`
                  <svg width="${size}" height="${size}" viewBox="0 0 ${size} ${size}" xmlns="http://www.w3.org/2000/svg">
                    <circle cx="${size/2}" cy="${size/2}" r="${size/2 - 2}" fill="${color}" stroke="white" stroke-width="3"/>
                    <text x="${size/2}" y="${size/2 + 6}" text-anchor="middle" fill="white" font-size="16" font-weight="bold" font-family="Arial, sans-serif">
                      ${count}
                    </text>
                  </svg>
                `),
                scaledSize: new google.maps.Size(size, size),
                anchor: new google.maps.Point(size/2, size/2)
              },
              title: `${count} stores in this area`,
              zIndex: 1000
            });
          }
        }
      });
    } else {
      // Fallback: add markers directly to map if clustering library not available
      storeMarkers.forEach(marker => marker.setMap(this.map));
    }

    // Fit map to bounds
    if (stores.length > 0) {
      this.map.fitBounds(bounds);

      // Apply mobile-specific zoom limits
      setTimeout(() => {
        const isMobile = window.innerWidth <= 768;
        const currentZoom = this.map.getZoom();

        if (stores.length === 1) {
          // Single store zoom levels
          const maxSingleZoom = isMobile ? 12 : 15;
          if (currentZoom > maxSingleZoom) {
            this.map.setZoom(maxSingleZoom);
          }
        } else {
          // Multiple stores zoom levels
          const maxMultiZoom = isMobile ? 8 : 12;
          if (currentZoom > maxMultiZoom) {
            this.map.setZoom(maxMultiZoom);
          }
        }
      }, 100);
    }

  }


  focusStore(store) {
    if (!this.map) return;

    this.map.panTo({ lat: store.lat, lng: store.lng });
    this.map.setZoom(15);

    // Find and trigger the marker click
    const storeIndex = this.filteredStores.length > 0
      ? this.filteredStores.findIndex(s => s.id === store.id)
      : this.stores.findIndex(s => s.id === store.id);

    if (storeIndex !== -1) {
      const markerIndex = storeIndex + (this.userLocation ? 1 : 0);
      if (this.markers[markerIndex]) {
        google.maps.event.trigger(this.markers[markerIndex], 'click');
      }
    }
  }

  clearMarkers() {
    // Clear clusterer if it exists
    if (this.markerClusterer) {
      this.markerClusterer.clearMarkers();
      this.markerClusterer = null;
    }

    // Clear individual markers
    this.markers.forEach(marker => marker.setMap(null));
    this.markers = [];
    this.infoWindows = [];
  }

  showLoading(show) {
    const spinner = document.querySelector('.loading-spinner');
    const grid = document.getElementById('stores-grid');
    const noResults = document.querySelector('.no-results');

    if (show) {
      if (spinner) spinner.style.display = 'block';
      if (grid) grid.innerHTML = '';
      if (noResults) noResults.style.display = 'none';
    } else {
      if (spinner) spinner.style.display = 'none';
    }
  }

  getMapStyles() {
    return [
      {
        featureType: 'poi',
        elementType: 'labels',
        stylers: [{ visibility: 'off' }]
      },
      {
        featureType: 'transit',
        elementType: 'labels',
        stylers: [{ visibility: 'off' }]
      }
    ];
  }
}

// Initialize when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
  window.storeLocator = new StoreLocator();
});

// Load Google Maps API with Places library and MarkerClusterer if key is available
function loadGoogleMapsAPI() {
  const dataElement = document.getElementById('store-locations-data');
  if (dataElement) {
    const data = JSON.parse(dataElement.textContent);
    if (data.api_key) {
      // Load MarkerClusterer library first
      const clustererScript = document.createElement('script');
      clustererScript.src = 'https://unpkg.com/@googlemaps/markerclusterer/dist/index.min.js';
      clustererScript.onload = () => {
        // After MarkerClusterer loads, load Google Maps API
        const mapsScript = document.createElement('script');
        mapsScript.src = `https://maps.googleapis.com/maps/api/js?key=${data.api_key}&libraries=places&callback=initMap`;
        mapsScript.async = true;
        mapsScript.defer = true;
        document.head.appendChild(mapsScript);
      };
      document.head.appendChild(clustererScript);
    }
  }
}

// Callback for Google Maps
window.initMap = function() {
  if (window.storeLocator) {
    window.storeLocator.initializeMap();
  }
};

// Load Google Maps on page load
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', loadGoogleMapsAPI);
} else {
  loadGoogleMapsAPI();
}